import React, { useEffect, useState, useCallback } from "react";
import { TableEmpty } from "@finance/components/DesignSystem";
import { api, fmtDate, errMsg } from "@finance/lib/api";
import Layout, { PageHeader } from "@finance/components/Layout";
import { useAuth } from "@finance/context/AuthContext";
import { Button } from "@finance/components/ui/button";
import { Input } from "@finance/components/ui/input";
import { Label } from "@finance/components/ui/label";
import { Textarea } from "@finance/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@finance/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@finance/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash, PencilSimple, FloppyDisk, Receipt, CheckCircle } from "@phosphor-icons/react";

const slug = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
const badge = (status) => status === "Paid" ? "bg-emerald-100 text-emerald-700" : status === "Approved" ? "bg-sky-100 text-sky-700" : status === "Rejected" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700";

function ExpenseDialog({ log, activity, onDone }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ amount_exclusive: "", tax_type: "Vatable", description: "", currency: "TZS" });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/admin/logs/${log.id}/raise-expense`, { ...f, amount_exclusive: Number(f.amount_exclusive) });
      toast.success("Expense request raised — sent to Finance for approval");
      setOpen(false); onDone();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };
  return (
    <>
      <button className="inline-flex items-center gap-1 rounded-sm border border-[#1B4383] px-2 py-0.5 text-xs font-semibold text-[#1B4383] hover:bg-[#1B4383] hover:text-white"
        data-testid={`raise-expense-${log.id}`} onClick={() => { setF({ amount_exclusive: "", tax_type: "Vatable", description: "", currency: "TZS" }); setOpen(true); }}>
        <Receipt size={13} /> Raise Expense
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="raise-expense-dialog">
          <DialogHeader><DialogTitle className="font-display">Raise Expense — {activity.title}</DialogTitle>
            <DialogDescription>Raise an expense request against this log entry; it goes to Finance for approval.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="rounded-sm bg-gray-50 px-3 py-2 text-xs text-gray-500">Tagged <b>Admin – {activity.title}</b>. It will appear in Finance → Expenses for approval and post to the GL when paid.</div>
            <div><Label className="lx-label">Description</Label><Input className="mt-1" data-testid="raise-exp-desc" placeholder={`Admin – ${activity.title}`} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="lx-label">Amount (excl. VAT)</Label><Input type="number" className="mt-1" data-testid="raise-exp-amount" value={f.amount_exclusive} onChange={(e) => setF({ ...f, amount_exclusive: e.target.value })} /></div>
              <div><Label className="lx-label">VAT Category</Label>
                <select className="mt-1 h-10 w-full rounded-sm border border-gray-300 px-2 text-sm" data-testid="raise-exp-tax" value={f.tax_type} onChange={(e) => setF({ ...f, tax_type: e.target.value })}>
                  {["Vatable", "Zero-Rated", "Exempt", "Not VATable"].map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="bg-[#1B4383] hover:bg-[#122E5C]" data-testid="raise-exp-submit" disabled={saving || !f.amount_exclusive} onClick={submit}>{saving ? "Raising…" : "Raise Expense"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function ActivityView({ activity, onProcSaved }) {
  const { user } = useAuth();
  const canEdit = user?.role_code === "HR" || user?.is_executive;
  const [logs, setLogs] = useState([]);
  const [adding, setAdding] = useState(false);
  const [row, setRow] = useState({});
  const [editSteps, setEditSteps] = useState(false);
  const [draft, setDraft] = useState(activity);

  const loadLogs = useCallback(() => {
    api.get("/admin/logs", { params: { activity_key: activity.key } }).then((r) => setLogs(r.data)).catch(() => {});
  }, [activity.key]);
  useEffect(() => { loadLogs(); setDraft(activity); setEditSteps(false); setAdding(false); setRow({}); }, [activity, loadLogs]);

  const cols = activity.log_columns || [];
  const addLog = async () => {
    const data = {}; cols.forEach((c) => { if (row[c] != null && row[c] !== "") data[c] = row[c]; });
    if (Object.keys(data).length === 0) { toast.error("Fill in at least one field."); return; }
    try {
      await api.post("/admin/logs", { activity_key: activity.key, date: row[cols[0]] || new Date().toISOString().slice(0, 10), data });
      toast.success("Log entry added"); setRow({}); setAdding(false); loadLogs();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const delLog = async (id) => {
    if (!window.confirm("Delete this log entry?")) return;
    try { await api.delete(`/admin/logs/${id}`); toast.success("Deleted"); loadLogs(); } catch (e) { toast.error(errMsg(e)); }
  };
  const saveSteps = async () => {
    try {
      const r = await api.put(`/admin/procedures/${activity.key}`, { sections: draft.sections, note: draft.note });
      toast.success("Procedure updated"); setEditSteps(false); onProcSaved(r.data);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const setSectionSteps = (si, text) => {
    setDraft((d) => { const s = d.sections.map((x, i) => i === si ? { ...x, steps: text.split("\n").filter((l) => l.trim()) } : x); return { ...d, sections: s }; });
  };

  return (
    <div data-testid={`activity-view-${activity.key}`}>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2 rounded-sm border border-gray-200 bg-white p-4">
        <div>
          <div className="font-display text-lg font-semibold text-gray-900">{activity.title}</div>
          <div className="mt-0.5 text-xs text-gray-500">Frequency: <b>{activity.frequency}</b> · Responsible: <b>{activity.responsible}</b> · Category: <b>{activity.category}</b>{activity.finance ? " · 💰 Finance-linked" : ""}</div>
        </div>
        {canEdit && (editSteps
          ? <div className="flex gap-2"><Button variant="outline" onClick={() => { setDraft(activity); setEditSteps(false); }}>Cancel</Button><Button className="bg-[#1B4383] hover:bg-[#122E5C]" data-testid="save-steps" onClick={saveSteps}><FloppyDisk size={15} className="mr-1" />Save Steps</Button></div>
          : <Button variant="outline" data-testid="edit-steps" onClick={() => setEditSteps(true)}><PencilSimple size={15} className="mr-1" />Edit Steps</Button>)}
      </div>

      {/* SOP steps */}
      <div className="mb-6 space-y-4 rounded-sm border border-gray-200 bg-white p-5">
        {draft.sections?.map((sec, si) => (
          <div key={si}>
            <div className="lx-label mb-2 text-gray-700">{sec.title}</div>
            {editSteps
              ? <Textarea rows={sec.steps.length + 1} data-testid={`steps-textarea-${si}`} defaultValue={sec.steps.join("\n")} onChange={(e) => setSectionSteps(si, e.target.value)} />
              : <ol className="list-decimal space-y-1 pl-5 text-sm text-gray-700">{sec.steps.map((s, i) => <li key={i}>{s}</li>)}</ol>}
          </div>
        ))}
        {editSteps
          ? <div><Label className="lx-label">Note (default)</Label><Textarea rows={2} className="mt-1" data-testid="note-textarea" defaultValue={draft.note || ""} onChange={(e) => setDraft((d) => ({ ...d, note: e.target.value }))} /></div>
          : (activity.note ? <div className="rounded-sm border border-blue-100 bg-blue-50/60 px-3 py-2 text-xs text-blue-800"><b>Note:</b> {activity.note}</div> : null)}
      </div>

      {/* Log */}
      <div className="rounded-sm border border-gray-200 bg-white">
        <div className="flex items-center justify-between border-b border-gray-100 px-4 py-2.5">
          <span className="lx-label text-gray-600">Log — record each time this is done ({logs.length})</span>
          <Button size="sm" variant="outline" data-testid="add-log-toggle" onClick={() => setAdding((v) => !v)}><Plus size={15} className="mr-1" />Add Entry</Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 lx-label text-gray-500">
              <tr>{cols.map((c) => <th key={c} className="whitespace-nowrap px-3 py-2 text-left">{c}</th>)}{activity.finance && <th className="px-3 py-2 text-left">Expense</th>}<th className="px-3 py-2"></th></tr>
            </thead>
            <tbody>
              {adding && (
                <tr className="border-t border-gray-100 bg-amber-50/40" data-testid="add-log-row">
                  {cols.map((c) => (
                    <td key={c} className="px-2 py-1.5">
                      <Input className="h-8 min-w-[9rem] text-sm" data-testid={`log-input-${slug(c)}`} type={/date/i.test(c) ? "date" : "text"} value={row[c] || ""} onChange={(e) => setRow({ ...row, [c]: e.target.value })} />
                    </td>
                  ))}
                  {activity.finance && <td />}
                  <td className="px-2 py-1.5 text-right"><Button size="sm" className="bg-[#1B4383] hover:bg-[#122E5C]" data-testid="save-log" onClick={addLog}>Save</Button></td>
                </tr>
              )}
              {logs.length === 0 && !adding && <TableEmpty colSpan={cols.length + 2} title="No entries yet" message="Logged procedure entries will appear here." />}
              {logs.map((l) => (
                <tr key={l.id} className="border-t border-gray-100" data-testid={`log-row-${l.id}`}>
                  {cols.map((c) => <td key={c} className="whitespace-nowrap px-3 py-2 text-gray-700">{/date/i.test(c) && l.data?.[c] ? fmtDate(l.data[c]) : (l.data?.[c] || "—")}</td>)}
                  {activity.finance && (
                    <td className="px-3 py-2">
                      {l.expense
                        ? <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ${badge(l.expense.status)}`} data-testid={`exp-status-${l.id}`}><CheckCircle size={12} />{l.expense.status}</span>
                        : <ExpenseDialog log={l} activity={activity} onDone={loadLogs} />}
                    </td>
                  )}
                  <td className="px-3 py-2 text-right"><button className="text-gray-300 hover:text-red-500" data-testid={`del-log-${l.id}`} onClick={() => delLog(l.id)}><Trash size={15} /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const DIR_COLS = {
  hotels: [["city", "City"], ["hotel_name", "Hotel"], ["contact_person", "Contact"], ["phone", "Phone"], ["email", "Email"], ["rate", "Rate/Night (TZS)"], ["preferred", "Preferred"]],
  "flight-agents": [["agent", "Agent/Airline"], ["contact_person", "Contact"], ["phone", "Phone"], ["email", "Email"], ["services", "Services / Routes"], ["status", "Status"]],
  "car-hire": [["city", "City"], ["company", "Company"], ["contact_person", "Contact"], ["phone", "Phone"], ["email", "Email"], ["vehicle_types", "Vehicle Types"], ["status", "Status"]],
};

function Directory({ kind }) {
  const cols = DIR_COLS[kind];
  const [rows, setRows] = useState([]);
  const [adding, setAdding] = useState(false);
  const [row, setRow] = useState({});
  const load = useCallback(() => { api.get(`/admin/directories/${kind}`).then((r) => setRows(r.data)).catch(() => {}); }, [kind]);
  useEffect(() => { load(); setAdding(false); setRow({}); }, [kind, load]);
  const add = async () => {
    try { await api.post(`/admin/directories/${kind}`, row); toast.success("Added"); setRow({}); setAdding(false); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (id) => { if (!window.confirm("Delete this row?")) return; try { await api.delete(`/admin/directories/${kind}/${id}`); load(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="rounded-sm border border-gray-200 bg-white" data-testid={`directory-${kind}`}>
      <div className="flex items-center justify-between border-b border-gray-100 px-4 py-2.5">
        <span className="lx-label text-gray-600">{kind.replace("-", " ")} ({rows.length})</span>
        <Button size="sm" variant="outline" data-testid={`dir-add-${kind}`} onClick={() => setAdding((v) => !v)}><Plus size={15} className="mr-1" />Add</Button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 lx-label text-gray-500"><tr>{cols.map(([, l]) => <th key={l} className="px-3 py-2 text-left">{l}</th>)}<th /></tr></thead>
          <tbody>
            {adding && (
              <tr className="border-t border-gray-100 bg-amber-50/40">
                {cols.map(([k]) => <td key={k} className="px-2 py-1.5"><Input className="h-8 min-w-[8rem] text-sm" data-testid={`dir-input-${k}`} value={row[k] || ""} onChange={(e) => setRow({ ...row, [k]: e.target.value })} /></td>)}
                <td className="px-2 py-1.5 text-right"><Button size="sm" className="bg-[#1B4383] hover:bg-[#122E5C]" data-testid={`dir-save-${kind}`} onClick={add}>Save</Button></td>
              </tr>
            )}
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-gray-100">
                {cols.map(([k]) => <td key={k} className="px-3 py-2 text-gray-700">{typeof r[k] === "boolean" ? (r[k] ? "Yes" : "No") : (r[k] || "—")}</td>)}
                <td className="px-3 py-2 text-right"><button className="text-gray-300 hover:text-red-500" data-testid={`dir-del-${r.id}`} onClick={() => del(r.id)}><Trash size={15} /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function AdminProceduresPanel({ embedded = false }) {
  const [procs, setProcs] = useState([]);
  const [sel, setSel] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get("/admin/procedures").then((r) => { setProcs(r.data); setSel((s) => s || r.data[0]?.key); })
      .catch((e) => setError(errMsg(e) || "You do not have access to this module."));
  }, []);
  const onProcSaved = (updated) => setProcs((ps) => ps.map((p) => p.key === updated.key ? updated : p));
  const active = procs.find((p) => p.key === sel);

  return (
    <div className={embedded ? "" : "p-8"}>
      {error ? (
        <div className="rounded-sm border border-dashed border-gray-200 p-10 text-center text-sm text-gray-400" data-testid="admin-no-access">{error}</div>
      ) : (
        <Tabs value={sel || ""} onValueChange={setSel} data-testid="admin-tabs">
          <TabsList className="flex h-auto flex-wrap justify-start gap-1">
            {procs.map((p) => <TabsTrigger key={p.key} value={p.key} data-testid={`tab-${p.key}`}>{p.order}. {p.title}</TabsTrigger>)}
            <TabsTrigger value="__directories" data-testid="tab-directories">Directories</TabsTrigger>
          </TabsList>
          {procs.map((p) => (
            <TabsContent key={p.key} value={p.key} className="mt-6">
              {active && active.key === p.key && <ActivityView activity={active} onProcSaved={onProcSaved} />}
            </TabsContent>
          ))}
          <TabsContent value="__directories" className="mt-6 space-y-6">
            <Directory kind="hotels" />
            <Directory kind="flight-agents" />
            <Directory kind="car-hire" />
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}

export default function AdminProcedures() {
  return (
    <Layout>
      <PageHeader title="Admin Procedures" subtitle="Office SOPs and logbooks per the approved admin procedures workbook. Finance-linked activities can raise expense requests." />
      <AdminProceduresPanel />
    </Layout>
  );
}
